import { GridColDef, GridRowsProp } from '@mui/x-data-grid';
import { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../app-context';
import IGridComponent from '../../../components/grid/i-grid.component';
import TitlePanel from '../../../components/title-panel/title-panel.component';
import { formatDateLong, formatDateTimeSmall, isEmpty, isEmptyArray } from '../../common/util.services';
import { getSyncStatus, syncData, deleteSyncStatus } from '../amazon.services';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, useMediaQuery, useTheme } from '@mui/system';
import HMobiledataIcon from '@mui/icons-material/HMobiledata';
import IDialogComponent from '../../../components/dialog/i-dialog.component';
import AmazonSyncHistory from './sync-history.component';
import YesNoDialog from '../../../components/message-dialog/yes-no-dialog.component';

const AmazonSyncStatus = () => {
  const appContext = useContext(AppContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const rows: GridRowsProp = [];
  const [syncStatusData, setSyncStatusData] = useState(rows);
  const [openHistory, setOpenHistory] = useState(false);
  const [showSyncMsg, setShowSyncMsg] = useState(false);
  const [attribute, setAttribute] = useState('');
  const navigate = useNavigate();
  const columns: GridColDef[] = [
    { field: 'attribute', headerName: 'Attribute', filterable: false, sortable: false, editable: false, width: 130 },
    { field: 'detail', headerName: 'Detail', filterable: false, sortable: false, editable: false, width: 450  },
    { field: 'startDate', headerName: 'Start Date', filterable: false, sortable: false, editable: false, width: 200,
      valueFormatter: (startDate: any) => isEmpty(startDate) ? 'NA' : formatDateLong(startDate)
    },
    { field: 'endDate', headerName: 'End Date', filterable: false, sortable: false, editable: false, width: 200,
      valueFormatter: (endDate: any) => isEmpty(endDate) ? 'NA' : formatDateLong(endDate)
    },
    { field: 'duration', headerName: 'Duration (Sec)', filterable: false, sortable: false, editable: false, width: 120,
      valueGetter: (value, row) => { return isEmpty(row.endDate) && isEmpty(row.startDate) ? 'NA' :
                                     dayjs(isEmpty(row.endDate) ? dayjs() : row.endDate).diff(dayjs(row.startDate), 'seconds')}
    },
    { field: 'status', headerName: 'Status', filterable: false, sortable: false, editable: false, width: 100  },
    { field: 'history', headerName: 'History', filterable: false, sortable: false, editable: false, width: 80,
      renderCell: (params) => (<Stack direction="row" alignItems="center" gap={1} justifyContent="center">
                                <HMobiledataIcon fontSize='small' color="primary"/></Stack>),
    }
  ];
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isEmptyArray(rows)) {
      setLoading(true);
      getSyncStatus(appContext,
        (response: any) => {
          setSyncStatusData(response.data);
          appContext.setShowLoader(false);
          setLoading(false);
        }, (error: any) => {
          console.log(error);
          appContext.setShowLoader(false);
          appContext.setErrorMessage(error.message);
          setLoading(false);
        });
    }
  }, []);

  const onOkClick = () => {
    setLoading(true);
    syncData(attribute, appContext,
      (response: any) => {
        appContext.setShowLoader(false);
        setLoading(false);
      }, (error: any) => {
        console.log(error);
        appContext.setShowLoader(false);
        appContext.setErrorMessage(error.message);
        setLoading(false);
      });
  }

  const syncAttributeData = (value: any) => {
    if('Order' === value.attribute || 'Sale' === value.attribute || 'Performance' === value.attribute || 'Transaction' === value.attribute) {
      appContext.setDialogMessage('Sync data from performance screen');
      return;
    }
    setAttribute(value.attribute);
    setShowSyncMsg(true); 
  }
  
  const deleteSync = (value: any) => {
    if(!isEmpty(value.rowId)) {
      deleteSyncStatus(value.rowId, appContext,
        (response: any) => {
          appContext.setShowLoader(false);
          setLoading(false);
          navigate(0);
        }, (error: any) => {
          console.log(error);
          appContext.setShowLoader(false);
          appContext.setErrorMessage(error.message);
          setLoading(false);
        });
    }
  }

  const handleCellClick = (value: any) => {
    const field = value.field;
    if('history' === field) {
      setAttribute(value.row.attribute);
      setOpenHistory(true);
    }
  }

  const closeHistory = () => {
    setOpenHistory(false);
  }

  const getMobilePanel = () => {
    const mobileColumns: GridColDef[] = [
    { field: 'attribute', headerName: 'Attribute', filterable: false, 
      sortable: false, editable: false, width: 105, disableColumnMenu: true },
    { field: 'status', headerName: 'Status', filterable: false,
      sortable: false, editable: false, width: 95, disableColumnMenu: true },
    { field: 'endDate', headerName: 'Date', filterable: false, 
      sortable: false, editable: false, width: 135, disableColumnMenu: true,
      valueFormatter: (endDate: any) => isEmpty(endDate) ? 'NA' : formatDateTimeSmall(endDate)}
    ];

    return <>
    <Box sx={{ fontSize: '20px', fontWeight: 'bold', color: 'darkblue' }}>SYNC STATUS</Box>
      <IGridComponent title={''} rows={syncStatusData} columns={mobileColumns} loading={loading} hideFooter={true}
        showAction={false} showAddBtn={false} getRowId={(row: any) => row.attribute} maxHeight={500}/>
    </>
  }

  const getMainPanel = () => {
    return <>
      <IGridComponent title={''} rows={syncStatusData} columns={columns} loading={loading} hideFooter={true}
        showAction={true} showAddBtn={false} showDeleteAction={true} showEditAction={false} showApproveAction={false}
        handleDeleteAction={deleteSync} cellClick={handleCellClick}
        showSyncAction={true} handleSyncAction={syncAttributeData} getRowId={(row: any) => row.attribute} maxHeight={500}/>
      <IDialogComponent onClose={closeHistory} open={openHistory} title={'Sync History'}
        dialogBody={<AmazonSyncHistory attribute={attribute} close={closeHistory}/>} />
      <YesNoDialog open={showSyncMsg} onClose={() => { setShowSyncMsg(false) }}
        onOk={() => { setShowSyncMsg(false); onOkClick(); }} isHorizontal={true} message={'Do you want to sync the data?'}/>
    </>
  }

  return !isMobile ? <TitlePanel title='Sync Status' panelBody={getMainPanel()} /> : getMobilePanel();
}

export default AmazonSyncStatus;